import { SystemParamsReducer } from './features/system-params/state/system-params.reducers';
import { SystemParamsGridService } from './features/system-params/system-params-grid/system-params-grid.service';
import { SystemParamsEffects } from './features/system-params/state/system-params.effects';
import {
  selectParamHasChanges,
  selectParamIsLoading,
} from './features/system-params/state/system-params.selectors';
import { SystemParamsHistoryGridService } from './features/system-params/system-params-card/ag-system-params-history-grid/system-params-history-grid.service';
import { ENsiDictCode } from './core/enums/nsi-dict-code.enum';
import { Routes } from '@angular/router';
import { inject } from '@angular/core';
import { provideState, Store } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { NoAccessComponent } from 'ngx-mt-ui-lib/components/no-access';
import { PageNotFoundComponent } from 'ngx-mt-ui-lib/components/page-not-found';
import { authGuard } from './core/guards/auth.guard';
import {
  administrativeHomeGuard,
  administrativePageGuard,
  administrativeExplanationGuard,
} from './core/guards/administrative-page.guard';
import { UserEffects } from './features/user/state/user.effects';
import { UserReducer } from './features/user/state/user.reducers';
import { UserGridService } from './features/user/user-grid/user-grid.service';
import {
  selectUserHasChanges,
  selectUserIsLoading,
} from './features/user/state/user.selectors';
import { canDeactivateWithModal } from './shared/functions/can-deactivate-with-modal.function';
import { IAppState } from './app.state';
import { SystemParamsApiService } from './core/api/system-params-api.service';
import { NotificationSettingsApiService } from './core/api/notification-settings-api.service';
import { SYSTEM_PARAMS_DATA_SOURCE } from './core/api/system-params-data-source';

export const routes: Routes = [
  { path: '', redirectTo: 'auth', pathMatch: 'full' },
  {
    path: 'auth',
    loadComponent: () =>
      import('./features/auth/auth.component').then((c) => c.AuthComponent),
    data: { titleKey: 'AUTH.PAGE_TITLE' },
  },
  {
    path: 'auth-code',
    loadComponent: () =>
      import('./features/auth/authentification/authentification.component').then(
        (c) => c.AuthentificationComponent,
      ),
  },
  {
    path: 'access-admin',
    canActivateChild: [authGuard, administrativePageGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        canActivate: [administrativeHomeGuard],
        loadComponent: () =>
          import('./features/auth/auth.component').then((c) => c.AuthComponent),
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./features/user/user-page.component').then(
            (c) => c.UserPageComponent,
          ),
        data: {
          titleKey: 'ADMIN.USERS.PAGE_BREADCRUMBS',
        },
        providers: [
          UserGridService,
          provideState('userAdmin', UserReducer),
          provideEffects(UserEffects),
        ],
        canDeactivate: [
          () => {
            const store = inject(Store<IAppState>);
            return store.selectSignal(selectUserIsLoading)()
              ? false
              : canDeactivateWithModal([store.select(selectUserHasChanges)]);
          },
        ],
      },
      {
        path: 'system-params',
        loadComponent: () =>
          import('./features/system-params/system-params-page.component').then(
            (c) => c.SystemParamsPageComponent,
          ),
        data: {
          titleKey: 'ADMIN.SYSTEM_PARAMS.PAGE_BREADCRUMBS',
        },
        providers: [
          SystemParamsGridService,
          SystemParamsHistoryGridService,
          {
            provide: SYSTEM_PARAMS_DATA_SOURCE,
            useExisting: SystemParamsApiService,
          },
          provideState('systemParamsAdmin', SystemParamsReducer),
          provideEffects(SystemParamsEffects),
        ],
        canDeactivate: [
          () => {
            const store: Store<IAppState> = inject(Store);

            if (store.selectSignal(selectParamIsLoading)()) return false;
            return canDeactivateWithModal([
              store.select(selectParamHasChanges),
            ]);
          },
        ],
      },
      {
        path: 'nsi',
        data: {
          titleKey: 'ADMIN.NSI.PAGE_BREADCRUMBS',
        },
        loadComponent: () =>
          import('./features/nsi/nsi.component').then((c) => c.NsiComponent),
        children: [
          {
            path: ENsiDictCode.SUBSYSTEMS,
            loadComponent: () =>
              import('./features/nsi/nsi-dict/nsi-dict-subsystems/nsi-dict-subsystems.component').then(
                (c) => c.NsiDictSubsystemsComponent,
              ),
          },
        ],
      },
      {
        path: 'event-log',
        loadComponent: () =>
          import('./features/event-log/event-log.component').then(
            (c) => c.EventLogComponent,
          ),
        data: {
          titleKey: 'ADMIN.EVENT_LOG.PAGE_BREADCRUMBS',
        },
        children: [
          {
            path: ':id',
            loadComponent: () =>
              import('./features/event-log/event-log-card/event-log-card.component').then(
                (c) => c.EventLogCardComponent,
              ),
          },
        ],
      },
      {
        path: 'informing',
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'system-scenarios' },
          {
            path: 'system-scenarios',
            loadComponent: () =>
              import('./features/system-scenarios/system-scenarios.component').then(
                (c) => c.SystemScenariosComponent,
              ),
            data: { title: 'Системные сценарии' },
          },
          {
            path: 'mailings',
            loadComponent: () =>
              import('./features/email-history/mailing-history.component').then(
                (c) => c.MailingHistoryComponent,
              ),
            data: { title: 'Отправки' },
          },
          {
            path: 'settings',
            loadComponent: () =>
              import('./features/system-params/system-params-page.component').then(
                (c) => c.SystemParamsPageComponent,
              ),
            data: { title: 'Настройки информирования' },
            providers: [
              SystemParamsGridService,
              SystemParamsHistoryGridService,
              {
                provide: SYSTEM_PARAMS_DATA_SOURCE,
                useExisting: NotificationSettingsApiService,
              },
              provideState('systemParamsAdmin', SystemParamsReducer),
              provideEffects(SystemParamsEffects),
            ],
            canDeactivate: [
              () => {
                const store: Store<IAppState> = inject(Store);

                if (store.selectSignal(selectParamIsLoading)()) return false;
                return canDeactivateWithModal([
                  store.select(selectParamHasChanges),
                ]);
              },
            ],
          },
        ],
      },
      {
        path: 'notifications',
        redirectTo: 'informing/mailings',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'main',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/subsystems/subsystems.component').then(
        (c) => c.SubsystemsComponent,
      ),
    data: { titleKey: 'SUBSYSTEMS.PAGE_TITLE' },
  },
  {
    path: 'account',
    canActivate: [authGuard],
    data: { titleKey: 'ACCOUNT.PAGE_TITLE' },
    loadComponent: () =>
      import('./features/account/account.component').then(
        (c) => c.AccountComponent,
      ),
  },
  {
    path: 'page-not-found',
    component: PageNotFoundComponent,
    data: { titleKey: 'PAGE_NOT_FOUND.PAGE_TITLE' },
  },
  {
    path: 'no-access',
    component: NoAccessComponent,
    data: { titleKey: 'NO_ACCESS.PAGE_TITLE' },
  },
  {
    path: 'administration-required',
    canActivate: [authGuard, administrativeExplanationGuard],
    loadComponent: () =>
      import('./features/auth/administration-required/administration-required.component').then(
        (c) => c.AdministrationRequiredComponent,
      ),
    data: { titleKey: 'NO_ACCESS.PAGE_TITLE' },
  },
  { path: '**', redirectTo: 'page-not-found', pathMatch: 'full' },
];
